/**
 * ChatMessageRenderer.jsx  — v5
 * ==============================
 * STRICT colour rules — no yellow on numbers:
 *   -€123 / -12%  → red   (#f85149)
 *   +€123 / +12%  → green (#3fb950)
 *   €123          → white (plain currency, no colour)
 *   42%           → white (plain percentage, no colour)
 *   UP / DOWN     → green / red
 *   RISK badges   → coloured pill as before
 *   Section headers → gold left-bar + label + gradient line (no badge)
 */

import React, { useMemo } from "react";
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

/* ── Design tokens ─────────────────────────────────────────────────────── */
const T = {
  bg:          "#2f2f2f",
  surface:     "#252323",
  surfaceAlt:  "#201f1f",
  surfaceHov:  "#202020",
  border:      "#2a2a2a",
  borderStrong:"#3a3a3a",
  borderGold:  "#bca97a",
  gold:        "#e2bf6d",   /* used ONLY for section headers, accent bars, table headings */
  goldDim:     "#7a5c1a",
  text:        "#ffffff",
  textMuted:   "#a0a0a0",
  textDim:     "#555555",
  green:       "#68d877",
  red:         "#ef4646",
  amber:       "#d29922",   /* multipliers like 2.3x */
  purple:      "#e48bfa",   /* plain € currency */
  riskExtreme: { text: "#f85149", bg: "#200d0d", border: "#6b1a1a" },
  riskHigh:    { text: "#e3b341", bg: "#261d05", border: "#6b4f10" },
  riskMedium:  { text: "#d29922", bg: "#201908", border: "#6b5010" },
  riskLow:     { text: "#3fb950", bg: "#0d2016", border: "#1a5e3c" },
  rank1: { bg: "#1e1600", border: "#e2bf6d", text: "#ffd700" },
  rank2: { bg: "#1a1a1a", border: "#a0a0a0", text: "#c0c0c0" },
  rank3: { bg: "#180f00", border: "#cd7f32", text: "#cd7f32" },
  fontBody: "'Georgia', 'Times New Roman', serif",
  fontMono: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace",
  fontUI:   "'Trebuchet MS', 'Gill Sans', system-ui, sans-serif",
};

/* ── Helpers ───────────────────────────────────────────────────────────── */
const isTableRow      = l => l.includes(" | ") || (l.startsWith("| ") && l.endsWith(" |"));
const isBullet        = l => /^[-•*]\s/.test(l.trim());
const isNumberedItem  = l => /^\d+\.\s/.test(l.trim());
const isSectionHeader = l => {
  const t = l.trim();
  return (
    t.endsWith(":") &&
    t.length < 80 &&
    !t.includes("|") &&
    !t.match(/€[\d,]+/) &&
    !t.match(/[+-]?\d+%/) &&
    !t.match(/^\d/)
  );
};
const parseStatLine = l => {
  const m = l.match(/^([^:]+):\s*(.+)$/);
  return m ? { label: m[1].trim(), value: m[2].trim() } : null;
};
const parseTable = lines => {
  const rows = lines
    .filter(l => l.trim() && !l.match(/^[-|:]+$/))
    .map(l => l.replace(/^\|/, "").replace(/\|$/, "").split("|").map(c => c.trim()));
  return rows.length < 2 ? null : { headers: rows[0], rows: rows.slice(1) };
};
const getRiskLevel = t =>
  /EXTREME/i.test(t) ? "extreme" :
  /HIGH/i.test(t)    ? "high"    :
  /MEDIUM/i.test(t)  ? "medium"  :
  /LOW/i.test(t)     ? "low"     : null;

/* ── InlineText ────────────────────────────────────────────────────────── */
/*
  Token priority (order in regex matters):
    1. -€...   → red
    2. +€...   → green
    3. €...    → white   ← NO yellow
    4. +N%     → green
    5. -N%     → red
    6. N%      → white   ← NO yellow
    7. Nx      → amber (multiplier)
    8. UP/DOWN → green/red
    9. RISK    → badge
   10. rest    → 
*/
function InlineText({ text }) {
  if (!text) return null;

  const tokens = text.split(
    /(-€[\d,]+(?:\.\d+)?(?:k|m)?|\+€[\d,]+(?:\.\d+)?(?:k|m)?|€[\d,]+(?:\.\d+)?(?:k|m)?|[+]\d+(?:\.\d+)?%|[-]\d+(?:\.\d+)?%|\d+(?:\.\d+)?%|\d+(?:\.\d+)?x|\bUP\b|\bDOWN\b|EXTREME RISK|HIGH RISK|MEDIUM RISK|LOW RISK)/gi
  );

  return (
    <>
      {tokens.map((tok, i) => {
        /* Signed currency */
        if (/^-€/.test(tok))
          return <span key={i} style={{ color: T.red,   fontWeight: 700, fontFamily: T.fontMono, fontSize: "0.9em" }}>{tok}</span>;
        if (/^\+€/.test(tok))
          return <span key={i} style={{ color: T.green, fontWeight: 700, fontFamily: T.fontMono, fontSize: "0.9em" }}>{tok}</span>;

        /* Plain currency → purple */
        if (/^€/.test(tok))
          return <span key={i} style={{ color: T.green, fontWeight: 600, fontFamily: T.fontMono, fontSize: "0.9em" }}>{tok}</span>;

        /* Signed percentages */
        if (/^\+\d+(\.\d+)?%$/.test(tok))
          return <span key={i} style={{ color: T.green, fontWeight: 700, fontFamily: T.fontMono, fontSize: "0.88em" }}>{tok}</span>;
        if (/^-\d+(\.\d+)?%$/.test(tok))
          return <span key={i} style={{ color: T.red,   fontWeight: 700, fontFamily: T.fontMono, fontSize: "0.88em" }}>{tok}</span>;

        /* Plain percentage → WHITE, no yellow */
        if (/^\d+(\.\d+)?%$/.test(tok))
          return <span key={i} style={{ color: T.text, fontFamily: T.fontMono, fontSize: "0.88em" }}>{tok}</span>;

        /* Multiplier (2.3x) */
        if (/^\d+(\.\d+)?x$/i.test(tok))
          return <span key={i} style={{ color: T.amber, fontWeight: 700, fontFamily: T.fontMono, fontSize: "0.88em" }}>{tok}</span>;

        /* UP / DOWN */
        if (/^UP$/i.test(tok))   return <span key={i} style={{ color: T.green, fontWeight: 700 }}>▲ UP</span>;
        if (/^DOWN$/i.test(tok)) return <span key={i} style={{ color: T.red,   fontWeight: 700 }}>▼ DOWN</span>;

        /* Risk badges */
        const rl = getRiskLevel(tok);
        if (rl) {
          const r = T[`risk${rl.charAt(0).toUpperCase() + rl.slice(1)}`];
          return (
            <span key={i} style={{
              background: r.bg, color: r.text, border: `1px solid ${r.border}`,
              borderRadius: 3, padding: "1px 7px", fontSize: "0.75em", fontWeight: 800,
              fontFamily: T.fontUI, letterSpacing: "0.07em", textTransform: "uppercase",
              verticalAlign: "middle",
            }}>
              {tok.toUpperCase()}
            </span>
          );
        }

        return <span key={i} style={{ color: T.text }}>{tok}</span>;
      })}
    </>
  );
}

/* ── SectionHeader — gold bar + label + gradient line, NO pill/badge ───── */
function SectionHeader({ text }) {
  return (
    <div style={{ marginTop: 22, marginBottom: 10, display: "flex", alignItems: "center", gap: 9 }}>
      <div style={{ width: 3, height: 14, background: T.gold, borderRadius: 2, flexShrink: 0 }} />
      <span style={{
        fontSize: 10, fontFamily: T.fontUI, fontWeight: 700,
        color: T.gold, letterSpacing: "0.13em", textTransform: "uppercase", whiteSpace: "nowrap",
      }}>
        {text.replace(/:$/, "")}
      </span>
      <div style={{ flex: 1, height: 1, background: `linear-gradient(to right, ${T.borderGold}55, transparent)` }} />
    </div>
  );
}

/* ── MetricCard ────────────────────────────────────────────────────────── */
function MetricCard({ label, value }) {
  const isNeg = /DOWN|-€/.test(value);
  const isPos = /\bUP\b|\+€/.test(value);
  const accent = isNeg ? T.red : isPos ? T.green : T.gold;
  return (
    <div style={{
      background: T.surface, border: `1px solid ${T.border}`,
      borderTop: `2px solid ${accent}`, borderRadius: 6,
      padding: "10px 13px", flex: "1 1 120px", minWidth: 100,
    }}>
      <div style={{ fontSize: 9, fontFamily: T.fontUI, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 17, fontFamily: T.fontMono, fontWeight: 700, color: T.text }}>
        <InlineText text={value} />
      </div>
    </div>
  );
}

/* ── DataTable ─────────────────────────────────────────────────────────── */
function DataTable({ headers, rows }) {
  return (
    <div style={{ overflowX: "auto", margin: "10px 0 14px", border: `1px solid ${T.border}`, borderRadius: 7 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.fontMono, fontSize: 12, color: T.text }}>
        <thead>
          <tr style={{ background: T.surfaceAlt, borderBottom: `1px solid ${T.borderStrong}` }}>
            {headers.map((h, i) => (
              <th key={i} style={{
                padding: "9px 12px", textAlign: i === 0 ? "left" : "right",
                fontSize: 9, fontFamily: T.fontUI, color: T.gold, fontWeight: 700,
                letterSpacing: "0.1em", textTransform: "uppercase", whiteSpace: "nowrap",
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri}
              style={{ background: ri % 2 === 0 ? T.bg : T.surface, borderBottom: `1px solid ${T.border}`, transition: "background 0.12s" }}
              onMouseEnter={e => (e.currentTarget.style.background = T.surfaceHov)}
              onMouseLeave={e => (e.currentTarget.style.background = ri % 2 === 0 ? T.bg : T.surface)}>
              {row.map((cell, ci) => (
                <td key={ci} style={{ padding: "7px 12px", textAlign: ci === 0 ? "left" : "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                  <InlineText text={cell} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── RankingTable ──────────────────────────────────────────────────────── */
function RankingTable({ items }) {
  const medals = ["🥇", "🥈", "🥉"];
  return (
    <div style={{ overflowX: "auto", margin: "8px 0 14px", border: `1px solid ${T.border}`, borderRadius: 7, overflow: "hidden" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.fontMono, fontSize: 12 }}>
        <tbody>
          {items.map((item, ri) => {
            const rs    = ri < 3 ? T[`rank${ri + 1}`] : null;
            const rowBg = rs ? rs.bg : ri % 2 === 0 ? T.bg : T.surface;
            const split = item.match(/^(.+?)\s*[-–:]\s+(€.+)$/);
            const name  = split ? split[1].trim() : item;
            const val   = split ? split[2].trim() : null;
            return (
              <tr key={ri}
                style={{ background: rowBg, borderLeft: rs ? `3px solid ${rs.border}` : "3px solid transparent", borderBottom: `1px solid ${T.border}`, transition: "background 0.12s" }}
                onMouseEnter={e => (e.currentTarget.style.background = T.surfaceHov)}
                onMouseLeave={e => (e.currentTarget.style.background = rowBg)}>
                <td style={{ padding: "9px 10px", width: 40, textAlign: "center", verticalAlign: "middle", whiteSpace: "nowrap" }}>
                  {ri < 3
                    ? <span style={{ fontSize: 18 }}>{medals[ri]}</span>
                    : <span style={{ fontFamily: T.fontMono, fontSize: 10, color: T.textMuted, fontWeight: 700 }}>#{ri + 1}</span>}
                </td>
                <td style={{ padding: "9px 12px", color: rs ? rs.text : T.text, fontWeight: rs ? 600 : 400, verticalAlign: "middle" }}>
                  <InlineText text={name} />
                </td>
                {val && (
                  <td style={{ padding: "9px 12px", textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", verticalAlign: "middle" }}>
                    <InlineText text={val} />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ── BulletItem ────────────────────────────────────────────────────────── */
function BulletItem({ text, ordered, index }) {
  const content = text.replace(/^[-•*]\s*|\d+\.\s*/, "").trim();
  return (
    <div style={{ display: "flex", gap: 9, padding: "3px 0", alignItems: "flex-start", fontSize: 13, lineHeight: 1.65, color: T.text }}>
      {ordered
        ? <span style={{ color: T.gold, fontFamily: T.fontMono, fontSize: 11, fontWeight: 700, marginTop: 2, minWidth: 18, textAlign: "right", flexShrink: 0 }}>{index}.</span>
        : <span style={{ color: T.goldDim, marginTop: 7, flexShrink: 0, fontSize: 6 }}>◆</span>}
      <span><InlineText text={content} /></span>
    </div>
  );
}

/* ── ComparisonRow ─────────────────────────────────────────────────────── */
function ComparisonRow({ text }) {
  const parts = text.split("|").map(p => p.trim());
  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", padding: "7px 0", borderBottom: `1px solid ${T.border}` }}>
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span style={{ color: T.border, margin: "0 10px", fontSize: 14 }}>│</span>}
          <span style={{ fontSize: 13, color: T.text }}><InlineText text={part} /></span>
        </React.Fragment>
      ))}
    </div>
  );
}

/* ── Block parser ──────────────────────────────────────────────────────── */
function parseBlocks(text) {
  const lines = text.split("\n").map(l => l.trimEnd());
  const blocks = [];
  let i = 0;

  const isRankingList = items =>
    items.length >= 2 &&
    items.every((item, idx) => {
      const m = item.trim().match(/^(\d+)\.\s/);
      return m && parseInt(m[1]) === idx + 1;
    });

  while (i < lines.length) {
    const line = lines[i].trim();
    if (!line) { i++; continue; }

    /* Markdown headings */
    if (/^#{1,3}\s/.test(line)) {
      blocks.push({ type: "section_header", text: line.replace(/^#{1,3}\s/, "") + ":" }); i++; continue;
    }

    /* Pipe-delimited table */
    if (isTableRow(line)) {
      const tl = [];
      while (i < lines.length && (isTableRow(lines[i].trim()) || lines[i].trim().match(/^[-|:]+$/))) tl.push(lines[i++]);
      const parsed = parseTable(tl);
      if (parsed) blocks.push({ type: "table", ...parsed });
      continue;
    }

    /* Inline comparison (A | B | C on one line) */
    if (line.includes(" | ") && !line.startsWith("|")) {
      blocks.push({ type: "comparison", text: line }); i++; continue;
    }

    /* Section header */
    if (isSectionHeader(line)) {
      blocks.push({ type: "section_header", text: line }); i++; continue;
    }

    /* Numbered list */
    if (isNumberedItem(line)) {
      const items = [];
      while (i < lines.length) {
        const l = lines[i].trim();
        if (isNumberedItem(l))       { items.push(l); i++; }
        else if (!l && items.length) { i++; break; }
        else break;
      }
      blocks.push(
        isRankingList(items)
          ? { type: "ranking",         items: items.map(it => it.replace(/^\d+\.\s*/, "")) }
          : { type: "ordered_bullets", items }
      );
      continue;
    }

    /* Bullet list */
    if (isBullet(line)) {
      const items = [];
      while (i < lines.length && isBullet(lines[i].trim())) items.push(lines[i++].trim());
      blocks.push({ type: "bullets", items }); continue;
    }

    /* Metric cards — 2+ "Label: €value" lines */
    const stat = parseStatLine(line);
    if (stat && stat.value.match(/^[€\d+\-]/) && i + 1 < lines.length) {
      const nextStat = parseStatLine((lines[i + 1] || "").trim());
      if (nextStat) {
        const metrics = [];
        while (i < lines.length) {
          const ml = lines[i].trim();
          if (!ml) { i++; break; }
          const m = parseStatLine(ml);
          if (!m) break;
          metrics.push(m); i++;
        }
        if (metrics.length >= 2) { blocks.push({ type: "metric_cards", metrics }); continue; }
        blocks.push({ type: "text", text: metrics.map(m => `${m.label}: ${m.value}`).join("\n") }); continue;
      }
    }

    blocks.push({ type: "text", text: line }); i++;
  }
  return blocks;
}


/* ── CasinoChart ────────────────────────────────────────────────────────── */
/*  Renders a Claude-generated chart_spec using Recharts.                   */
/*  Colours match the existing dark gold casino theme (T tokens above).      */

const CHART_PALETTE = [
  T.gold, "#00A896", T.green, T.red, T.amber, T.purple,
  "#378ADD", "#7F77DD", "#F5A623", "#1D9E75",
];

function CasinoChart({ spec }) {
  if (!spec || !spec.type || !Array.isArray(spec.data) || spec.data.length === 0) return null;

  const chartData = spec.data.map(d => ({
    name: String(d.label),
    value: Number(d.value) || 0,
  }));

  const axisStyle = {
    fontSize: 10,
    fill: T.textMuted,
    fontFamily: T.fontUI,
  };
  const tooltipStyle = {
    backgroundColor: T.surface,
    border: `1px solid ${T.borderStrong}`,
    borderRadius: 6,
    color: T.text,
    fontSize: 11,
    fontFamily: T.fontUI,
  };
  const gridColor = T.border;
  const margin = { top: 8, right: 16, left: 0, bottom: 44 };

  const fmt = v => Number(v).toLocaleString();

  let chart = null;

  if (spec.type === "bar") {
    chart = (
      <BarChart data={chartData} margin={margin}>
        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
        <XAxis dataKey="name" tick={axisStyle} angle={-30} textAnchor="end" interval={0} />
        <YAxis tick={axisStyle} label={spec.y_label ? { value: spec.y_label, angle: -90, position: "insideLeft", fill: T.textMuted, fontSize: 10 } : undefined} />
        <Tooltip contentStyle={tooltipStyle} formatter={v => [fmt(v), spec.y_label || "Value"]} />
        <Bar dataKey="value" fill={T.gold} radius={[3, 3, 0, 0]} />
      </BarChart>
    );
  } else if (spec.type === "line") {
    chart = (
      <LineChart data={chartData} margin={margin}>
        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
        <XAxis dataKey="name" tick={axisStyle} angle={-30} textAnchor="end" interval={0} />
        <YAxis tick={axisStyle} />
        <Tooltip contentStyle={tooltipStyle} formatter={v => [fmt(v), spec.y_label || "Value"]} />
        <Line type="monotone" dataKey="value" stroke={T.gold} strokeWidth={2} dot={{ r: 3, fill: T.gold }} activeDot={{ r: 5 }} />
      </LineChart>
    );
  } else if (spec.type === "area") {
    chart = (
      <AreaChart data={chartData} margin={margin}>
        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
        <XAxis dataKey="name" tick={axisStyle} angle={-30} textAnchor="end" interval={0} />
        <YAxis tick={axisStyle} />
        <Tooltip contentStyle={tooltipStyle} formatter={v => [fmt(v), spec.y_label || "Value"]} />
        <Area type="monotone" dataKey="value" stroke={T.gold} fill={T.goldSoft} strokeWidth={2} />
      </AreaChart>
    );
  } else if (spec.type === "pie") {
    chart = (
      <PieChart>
        <Pie
          data={chartData}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          outerRadius={85}
          label={({ name, percent }) =>
            `${name.length > 12 ? name.slice(0, 11) + "…" : name} ${(percent * 100).toFixed(0)}%`
          }
          labelLine={false}
        >
          {chartData.map((_, i) => (
            <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
          ))}
        </Pie>
        <Tooltip contentStyle={tooltipStyle} formatter={v => fmt(v)} />
      </PieChart>
    );
  }

  if (!chart) return null;

  return (
    <div style={{
      marginTop: 14,
      background: T.surfaceAlt,
      border: `1px solid ${T.borderStrong}`,
      borderRadius: 8,
      padding: "12px 6px 6px",
    }}>
      {spec.title && (
        <div style={{
          fontSize: 11,
          color: T.textMuted,
          fontFamily: T.fontUI,
          paddingLeft: 10,
          marginBottom: 6,
          letterSpacing: "0.03em",
        }}>
          {spec.title}
        </div>
      )}
      <ResponsiveContainer width="100%" height={230}>
        {chart}
      </ResponsiveContainer>
    </div>
  );
}

/* ── Main renderer ─────────────────────────────────────────────────────── */
export default function ChatMessageRenderer({ text, chartSpec }) {
  const blocks = useMemo(() => parseBlocks(text || ""), [text]);
  if (!text) return null;
  return (
    <div style={{ color: T.text, fontFamily: T.fontBody, fontSize: 13, lineHeight: 1.7, maxWidth: "100%" }}>
      {blocks.map((block, bi) => {
        switch (block.type) {
          case "section_header":
            return <SectionHeader key={bi} text={block.text} />;
          case "metric_cards":
            return (
              <div key={bi} style={{ display: "flex", flexWrap: "wrap", gap: 7, margin: "9px 0 13px" }}>
                {block.metrics.map((m, mi) => <MetricCard key={mi} label={m.label} value={m.value} />)}
              </div>
            );
          case "table":
            return <DataTable key={bi} headers={block.headers} rows={block.rows} />;
          case "ranking":
            return <RankingTable key={bi} items={block.items} />;
          case "comparison":
            return <ComparisonRow key={bi} text={block.text} />;
          case "bullets":
            return (
              <div key={bi} style={{ margin: "4px 0 10px" }}>
                {block.items.map((item, ii) => <BulletItem key={ii} text={item} />)}
              </div>
            );
          case "ordered_bullets":
            return (
              <div key={bi} style={{ margin: "4px 0 10px" }}>
                {block.items.map((item, ii) => <BulletItem key={ii} text={item} ordered index={ii + 1} />)}
              </div>
            );
          default:
            return (
              <p key={bi} style={{ margin: "3px 0 7px", color: T.text, fontSize: 13 }}>
                <InlineText text={block.text} />
              </p>
            );
        }
      })}
      <CasinoChart spec={chartSpec} />
    </div>
  );
}